import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FaExternalLinkAlt, FaGithub, FaRegStar, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'sonner';
import LoadingButton from "../utils/Loading";
import LoadingAction from '../utils/LoadingAction';

const ProjectCard = ({
  image,
  title,
  description,
  demoCredentials,
  category,
  technologies,
  liveUrl,
  sourceUrl,
  featured = false,
  delay = 0,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadCode, setLoadCode] = useState(false);
  const navigate = useNavigate();

  const handleLiveDemoClick = (link) => {
    LoadingAction(
      setLoading,
      () => {
        const newWindow = window.open(link, "_blank",  "noopener,noreferrer");
        if (newWindow) {
          newWindow.opener = null;
        }
      }
    )
  }

  const handleLiveCodeClick = (link) => {
    if (!link || link === "private") {
      LoadingAction(
        setLoadCode,
        () => {
          navigate("/contact");
          toast.info(`This repository is private. Feel free to contact me if you'd like a walkthrough or wish to discuss the implementation.`);
        }
      )
    } else {
      LoadingAction(
        setLoadCode, 
        () => {
          const newWindow = window.open(link, "_blank", "noopener,noreferrer");
          if (newWindow) {
            newWindow.opener(null);
          }
        }
      )
    }
  }

  return (
    <div
      className="col-lg-4 col-md-6 mb-4"
      data-aos="fade-up"
      data-aos-delay={delay}
    >
      <div
        className={`project-card ${isHovered ? 'hovered' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="project-image-wrapper">
          <img
            src={image}
            alt={title}
            className="project-image"
            loading="lazy"
          />
          <div className="project-overlay">
            {featured && (
              <span className="featured-badge">
                <FaRegStar size={12} className="mr-1" />
                Featured
              </span>
            )}
            <div className="project-links">
              {liveUrl && (
                <LoadingButton
                  isLoading={loading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLiveDemoClick(liveUrl);
                  }}
                  className="btn btn-primary rounded-pill shadow"
                >
                  <FaExternalLinkAlt size={14} className="mr-1" />
                  Live Demo
                </LoadingButton>
              )}
              {sourceUrl && (
                <LoadingButton
                  isLoading={loadCode}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLiveCodeClick(sourceUrl);
                  }}
                  className="btn btn-outline-primary rounded-pill shadow"
                >
                  <FaGithub size={14} className="mr-1" />
                  Source
                </LoadingButton>
              )}
            </div>
          </div>
        </div>
        <div className="project-content">
          <span className="project-category">{category}</span>
          <h4 className="project-title">{title}</h4>
          <p className="project-description">{description}</p>
          {demoCredentials && (
            <div className="demo-box">
              <h5>Demo Login</h5>
              <p>
                <strong>Email:</strong> {demoCredentials.email}<br/>
                <strong>Password:</strong> {demoCredentials.password}
              </p>
              <div className="demo-box-note">
                <FaExclamationTriangle className="text-warning mr-1" />
                <strong>Demo Account</strong>
                <p className="mb-3">
                  This is a shared demo account. Data may be modified or reset periodically.
                </p>
              </div>
            </div>
          )}
          <div className="project-technologies">
            {technologies.map((tech, index) => (
              <span key={index} className={`tech-badge tech-${tech.toLowerCase().replace(/[.\s]/g, '-')}`}>
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

ProjectCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  demoCredentials: PropTypes.shape({
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
  }),
  category: PropTypes.string.isRequired,
  technologies: PropTypes.arrayOf(PropTypes.string).isRequired,
  liveUrl: PropTypes.string,
  sourceUrl: PropTypes.string,
  featured: PropTypes.bool,
  delay: PropTypes.number,
};

export default ProjectCard;
